import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  useLedgerAllocationCategorizationRulesQuery,
  useCreateLedgerAllocationCategorizationRule,
  useUpdateLedgerAllocationCategorizationRule,
  useDeleteLedgerAllocationCategorizationRule,
} from "../../hooks/useAccountingQueries";
import {
  extractApiErrorMessage,
  makeEmptyRuleCondition,
  normalizeCurrencyTyping,
} from "./utils";
import type { LocalCategorizationRule, LocalRuleCondition } from "./utils";
import type { LedgerAllocationOperationType } from "../../types/accounting.types";

type UseCategorizationRulesParams = {
  open: boolean
  ascOperationTypes: LedgerAllocationOperationType[]
  aepOperationTypes: LedgerAllocationOperationType[]
};

type OpenRulesManagerParams = {
  initialBudgetType: "ASC" | "AEP"
  currentCategoryId: string
  currentCategoryName: string
  defaultKeyword: string
};

export const useCategorizationRules = ({
  open,
  ascOperationTypes,
  aepOperationTypes,
}: UseCategorizationRulesParams) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [isRulesManagerOpen, setIsRulesManagerOpen] = useState(false);
  const [ruleActionInProgress, setRuleActionInProgress] = useState<string | null>(null);
  const [ruleName, setRuleName] = useState("");
  const [ruleConditions, setRuleConditions] = useState<LocalRuleCondition[]>([makeEmptyRuleCondition("1")]);
  const [ruleBudgetType, setRuleBudgetType] = useState<"ASC" | "AEP">("ASC");
  const [ruleOperationTypeId, setRuleOperationTypeId] = useState("");

  // ---------------------------------------------------------------------------
  // Queries & mutations
  // ---------------------------------------------------------------------------

  const {
    data: categorizationRules = [],
    isLoading: isCategorizationRulesLoading,
  } = useLedgerAllocationCategorizationRulesQuery({}, open && isRulesManagerOpen);

  const createCategorizationRuleMutation = useCreateLedgerAllocationCategorizationRule();
  const updateCategorizationRuleMutation = useUpdateLedgerAllocationCategorizationRule();
  const deleteCategorizationRuleMutation = useDeleteLedgerAllocationCategorizationRule();

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const sortedLocalRules = useMemo<LocalCategorizationRule[]>(
    () => categorizationRules.map((rule) => ({
      id: rule.id,
      name: rule.name,
      conditions: (Array.isArray(rule.conditions) ? rule.conditions : []) as LocalRuleCondition[],
      budgetType: (rule.budgetType === "AEP" ? "AEP" : "ASC"),
      operationTypeId: rule.operationTypeId ?? "",
      operationTypeName: rule.operationTypeName ?? "",
      isActive: Boolean(rule.active),
    })).sort((left, right) => {
      if (left.isActive !== right.isActive) return left.isActive ? -1 : 1;
      return left.name.localeCompare(right.name, "fr");
    }),
    [categorizationRules],
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const openRulesManager = ({
    initialBudgetType,
    currentCategoryId,
    currentCategoryName,
    defaultKeyword,
  }: OpenRulesManagerParams) => {
    setRuleBudgetType(initialBudgetType);
    setRuleOperationTypeId(currentCategoryId);
    setRuleConditions([{ ...makeEmptyRuleCondition(), textValue: defaultKeyword }]);
    setRuleName(currentCategoryName ? `Règle - ${currentCategoryName}` : "");
    setIsRulesManagerOpen(true);
  };

  const updateRuleCondition = (conditionId: string, patch: Partial<LocalRuleCondition>) => {
    setRuleConditions((prev) => prev.map((c) => (c.id === conditionId ? { ...c, ...patch } : c)));
  };

  const addRuleCondition = () => {
    setRuleConditions((prev) => [...prev, makeEmptyRuleCondition(String(prev.length + 1))]);
  };

  const removeRuleCondition = (conditionId: string) => {
    setRuleConditions((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((c) => c.id !== conditionId);
    });
  };

  const handleCreateLocalRule = async () => {
    setRuleActionInProgress("create");
    const name = ruleName.trim();
    const normalizedConditions = ruleConditions
      .map((c) => ({
        ...c,
        textValue: c.textValue.trim(),
        amountValue: normalizeCurrencyTyping(c.amountValue),
        amountValueTo: normalizeCurrencyTyping(c.amountValueTo),
      }))
      .filter((c) => {
        if (c.kind === "keyword") return Boolean(c.textValue);
        if (c.kind === "bank_account") return Boolean(c.bankAccountId);
        if (c.amountOperator === "between") return Boolean(c.amountValue) && Boolean(c.amountValueTo);
        return Boolean(c.amountValue);
      });
    const availableOps = ruleBudgetType === "AEP" ? aepOperationTypes : ascOperationTypes;
    const selectedOp = availableOps.find((op) => op.id === ruleOperationTypeId);

    if (!name || normalizedConditions.length === 0 || !selectedOp) {
      toast.error("Nom, condition(s) et catégorie sont requis.");
      setRuleActionInProgress(null);
      return;
    }

    try {
      await createCategorizationRuleMutation.mutateAsync({
        name,
        budgetType: ruleBudgetType,
        operationTypeId: selectedOp.id,
        conditions: normalizedConditions,
        active: true,
      });
      toast.success("Règle ajoutée.");
      setRuleName("");
      setRuleConditions([makeEmptyRuleCondition()]);
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setRuleActionInProgress(null);
    }
  };

  const handleToggleLocalRuleActive = async (ruleId: string) => {
    const currentRule = sortedLocalRules.find((r) => r.id === ruleId);
    if (!currentRule) return;

    setRuleActionInProgress(`toggle-${ruleId}`);
    try {
      await updateCategorizationRuleMutation.mutateAsync({ id: ruleId, active: !currentRule.isActive });
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setRuleActionInProgress(null);
    }
  };

  const handleDeleteLocalRule = async (ruleId: string) => {
    setRuleActionInProgress(`delete-${ruleId}`);
    try {
      await deleteCategorizationRuleMutation.mutateAsync(ruleId);
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setRuleActionInProgress(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    isRulesManagerOpen,
    setIsRulesManagerOpen,
    ruleName,
    setRuleName,
    ruleConditions,
    updateRuleCondition,
    addRuleCondition,
    removeRuleCondition,
    ruleBudgetType,
    setRuleBudgetType,
    ruleOperationTypeId,
    setRuleOperationTypeId,
    ruleActionInProgress,
    sortedLocalRules,
    isCategorizationRulesLoading,
    openRulesManager,
    handleCreateLocalRule: () => void handleCreateLocalRule(),
    handleToggleLocalRuleActive: (ruleId: string) => void handleToggleLocalRuleActive(ruleId),
    handleDeleteLocalRule: (ruleId: string) => void handleDeleteLocalRule(ruleId),
  };
};
